import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { CapSkeletonImage } from './cap-skeleton'

/**
 * Imagen con su propio esqueleto hasta que termina de cargar. Réplica de `shared/ui/image-loader.tsx`.
 *
 * El esqueleto se apila ENCIMA y se saca al terminar, en vez de dibujarse en su lugar y cambiarse
 * por la imagen. La diferencia se nota: si se reemplaza, entre que el esqueleto se va y la imagen
 * pinta hay un frame de fondo desnudo, que se ve como un parpadeo blanco.
 *
 * ## Por qué además de `onLoad` se pregunta `img.complete`
 *
 * `onLoad` es un evento: si la imagen termina de decodificarse ANTES de que React monte el elemento
 * y le cuelgue el manejador —caché caliente, vuelta atrás del navegador— el evento se dispara
 * contra nadie y el esqueleto se queda puesto para siempre sobre una imagen que ya está ahí. Es un
 * riesgo conocido del `<img>`, no algo que se haya visto acá; el efecto que consulta `complete` al
 * montar lo cierra por dos renglones y no hay contra.
 *
 * ## `className` y `style` son del CONTENEDOR, no del `<img>`
 *
 * Como el `ImageLoader` de la app, donde van a la `View` que envuelve. La imagen se posiciona en
 * absoluto y llena esa caja, así que quien la dimensiona es el contenedor y sólo él.
 *
 * ☠️ Y no es una preferencia de estilo: cuando `style` viajaba al `<img>`, el `width` de cada foto
 * del carrusel llegaba a la imagen y el contenedor quedaba **sin ancho**. Las seis fotos cargaban
 * enteras y el héroe salía en blanco con su velo encima — un degradado gris que se lee como «no hay
 * fotos», que es exactamente la conclusión contraria a la verdadera.
 *
 * ## Y qué pasa si la URL falla
 *
 * Las URLs del preview vienen FIRMADAS y vencen (una hora la galería, cinco minutos la descarga).
 * Una firma vencida no tarda: contesta 403 y deja el ícono de imagen rota del navegador, que en una
 * previsualización se lee como «la app está rota». Por eso el error deja el esqueleto puesto —
 * honesto, y con la misma pinta que tendría mientras carga.
 */
export type CapImageProps = Omit<React.ComponentPropsWithoutRef<'img'>, 'onLoad' | 'onError'> & {
  /** `cover` (por defecto) o `contain`, como el `contentFit` de la app. */
  fit?: 'cover' | 'contain'
  /** Clases del CONTENEDOR. La imagen siempre llena su caja. */
  className?: string
}

type CapImageState = 'loading' | 'loaded' | 'failed'

/**
 * Sin `forwardRef`, a diferencia del resto de las primitivas: el ref del `<img>` lo necesita ESTE
 * componente para poder preguntarle `complete`. Reenviarlo además pediría fusionar dos refs para
 * una prop que nadie usa.
 */

export function CapImage({
  fit = 'cover',
  className,
  style,
  alt = '',
  src,
  ...props
}: CapImageProps) {
  const imageRef = useRef<HTMLImageElement>(null)
  const [state, setState] = useState<CapImageState>('loading')

  /**
   * `naturalWidth` y no sólo `complete`: una imagen que falló también queda `complete`, y sin
   * mirar el ancho la trataríamos como cargada y dibujaríamos el ícono de rota.
   */
  const settle = useCallback(() => {
    const image = imageRef.current
    if (!image?.complete) return
    setState(image.naturalWidth > 0 ? 'loaded' : 'failed')
  }, [])

  // Depende de `src`: al cambiar de foto hay que volver al esqueleto y preguntar por la nueva.
  useEffect(() => {
    setState('loading')
    settle()
  }, [src, settle])

  return (
    <div className={cn('relative overflow-hidden', className)} style={style}>
      <img
        ref={imageRef}
        alt={alt}
        src={src}
        className={cn(
          'absolute inset-0 h-full w-full',
          fit === 'cover' ? 'object-cover' : 'object-contain',
          state === 'loaded' ? 'opacity-100' : 'opacity-0'
        )}
        onLoad={() => setState('loaded')}
        onError={() => setState('failed')}
        {...props}
      />
      {state === 'loaded' ? null : <CapSkeletonImage className="absolute inset-0 rounded-none" />}
    </div>
  )
}
